import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { cartRowsSelector } from '../../ducks/cart.duck';
import { createCartRow } from '../../util/cart';

import { fetchCartShippingDelta } from './cartShippingDelta';

// Must match OrderPanel MODAL_BREAKPOINT / .openOrderForm hiding at --viewportLarge.
export const STICKY_BAR_MAX_WIDTH_PX = 1023;

// Long enough that dragging the quantity select or flipping delivery method does not
// fire a pair of requests per intermediate value.
export const CART_SHIPPING_DELTA_DEBOUNCE_MS = 300;

const stickyBarQuery = `(max-width: ${STICKY_BAR_MAX_WIDTH_PX}px)`;

const readStickyBarViewport = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return { known: false, isMobile: false };
  }
  return {
    known: true,
    isMobile: window.matchMedia(stickyBarQuery).matches,
  };
};

/**
 * Whether the sticky buy bar is in view (below the modal/desktop split).
 * Until the viewport is known, treat the bar as not yet ready so callers can hide
 * rather than flash a standalone shipping fee.
 *
 * @returns {{ known: boolean, isMobile: boolean }}
 */
export const useStickyBarViewport = () => {
  const [state, setState] = useState(readStickyBarViewport);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mq = window.matchMedia(stickyBarQuery);
    const sync = () => setState({ known: true, isMobile: mq.matches });
    sync();
    if (mq.addEventListener) {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  return state;
};

/**
 * Server delta for adding this listing to the current cart. Pickup does not fetch.
 *
 * @param {Object} params
 * @param {Object} params.listing
 * @param {string|number} [params.qty]
 * @param {string} [params.deliveryMethod]
 * @param {boolean} [params.enabled=true] skip the network when false (e.g. desktop sticky bar)
 * @returns {{
 *   status: 'idle'|'loading'|'ready'|'error',
 *   result: { delta: number, currency: string|null, combinable: boolean }|null
 * }}
 */
export const useCartShippingDelta = ({
  listing,
  qty,
  deliveryMethod,
  destination,
  enabled = true,
}) => {
  const cartRows = useSelector(cartRowsSelector);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('idle');

  const listingId = listing?.id?.uuid || null;
  const quantity = Number.parseInt(qty, 10);
  const isShipping = deliveryMethod === 'shipping';
  const hasChoices = Number.isInteger(quantity) && quantity > 0 && isShipping;
  const hasOtherRows = cartRows.some(row => row.listingId !== listingId);
  const willFetch = enabled && hasChoices && hasOtherRows;
  // Effects compare dependencies by identity, and the selector hands back a new array
  // every time. Key the fetch on what the request would actually contain instead.
  const rowsKey = cartRows
    .map(row => `${row.listingId}:${row.qty}:${row.deliveryMethod}`)
    .join('|');

  useEffect(() => {
    const candidateRow = willFetch
      ? createCartRow(listing, { qty: quantity, deliveryMethod })
      : null;

    if (!candidateRow || !willFetch) {
      setResult(null);
      setStatus('idle');
      return undefined;
    }

    // An in-flight quote shows nothing. Drop the previous result so a qty or
    // delivery change cannot keep advertising a stale delta through the debounce.
    setResult(null);
    setStatus('loading');

    let isCurrent = true;
    const timer = setTimeout(() => {
      fetchCartShippingDelta({ rows: cartRows, candidateRow, destination })
        .then(next => {
          if (isCurrent) {
            setResult(next);
            setStatus('ready');
          }
        })
        .catch(() => {
          if (isCurrent) {
            setResult(null);
            setStatus('error');
          }
        });
    }, CART_SHIPPING_DELTA_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listingId,
    quantity,
    deliveryMethod,
    willFetch,
    rowsKey,
    destination?.country,
    destination?.state,
  ]);

  const derivedStatus = willFetch && status === 'idle' && result == null ? 'loading' : status;

  return { status: derivedStatus, result };
};
